/* Tim & Akses — Multi Admin v1. Owner mengelola anggota + undangan. */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Copy, RotateCw, Trash2, Crown, Lock } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLE_LABEL = {
  TENANT_OWNER: "Pemilik", MANAGER: "Manager", ORDER_STAFF: "Order Staff",
  PRODUCT_STAFF: "Product Staff", FINANCE: "Finance", VIEWER: "Viewer",
};

function InviteModal({ open, onClose, roles, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("ORDER_STAFF");
  const [saving, setSaving] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const desc = roles.find((r) => r.role === role)?.description || "";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/team/invitations", { email, role });
      toast.success(data.email_sent ? "Undangan terkirim via email." : "Undangan dibuat. Email belum terkirim — salin link undangan di bawah.");
      setInviteUrl(data.invite_url || "");
      onInvited();
      if (data.email_sent) { setEmail(""); onClose(); }
    } catch (err) { notifyError(err); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setInviteUrl(""); setEmail(""); onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle>Undang Anggota Tim</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="staf@email.com" className="rounded-xl" data-testid="invite-email-input" />
          </div>
          <div>
            <Label>Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select"
              className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
              {roles.map((r) => <option key={r.role} value={r.role}>{ROLE_LABEL[r.role] || r.role}</option>)}
            </select>
            <p className="text-[11px] text-[#5C5C5C] mt-1.5" data-testid="invite-role-description">{desc}</p>
          </div>
          {inviteUrl && (
            <div className="rounded-xl bg-[#FAF9F6] border border-[#E5E5E5] p-3 space-y-2" data-testid="invite-link-box">
              <p className="text-[11px] text-[#5C5C5C]">Link undangan (berlaku 72 jam):</p>
              <div className="flex gap-2">
                <Input readOnly value={inviteUrl} className="rounded-lg text-xs h-8" data-testid="invite-link-input" />
                <Button type="button" size="sm" variant="outline" className="rounded-lg h-8"
                  onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Link disalin."); }}
                  data-testid="invite-link-copy"><Copy size={12} /></Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} className="brand-btn-primary rounded-xl" data-testid="invite-submit-btn">
              {saving ? "Mengirim…" : "Kirim Undangan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamAccess() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/team");
      setData(r.data);
    } catch (e) { notifyError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); } catch (e) { notifyError(e); }
  };

  if (!data) return <AdminLayout><div className="h-40 grid place-items-center text-sm text-[#5C5C5C]">Memuat…</div></AdminLayout>;

  const seatFull = data.available_seats <= 0;
  const isStarter = data.package === "STARTER";
  const used = data.active_staff_count + data.pending_count;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl font-black">Tim & Akses</h1>
          <p className="text-sm text-[#5C5C5C]">Kelola siapa saja yang dapat mengakses dashboard toko Anda.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} disabled={isStarter || seatFull}
          className="brand-btn-primary rounded-xl font-bold" data-testid="invite-open-btn">
          <UserPlus size={15} /> Undang Anggota
        </Button>
      </div>

      {/* Seat summary */}
      <Card className="p-4 rounded-2xl mb-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="team-seat-summary">
        <div><span className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold block">Paket</span>
          <span className="font-display font-black">{data.package}</span></div>
        <div><span className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold block">Staf Digunakan</span>
          <span className="font-display font-black" data-testid="team-seat-used">{used}/{data.seat_limit}</span>
          {data.members.filter((m) => !m.is_owner && !m.is_active).length > 0 && (
            <span className="text-[10px] text-[#5C5C5C] block">+{data.members.filter((m) => !m.is_owner && !m.is_active).length} nonaktif (tidak dihitung)</span>
          )}</div>
        <div><span className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold block">Sisa Seat</span>
          <span className="font-display font-black" data-testid="team-seat-available">{data.available_seats}</span></div>
        {(isStarter || seatFull) && (
          <div className="ml-auto flex items-center gap-3" data-testid="team-upgrade-cta">
            <span className="text-xs text-[#5C5C5C] flex items-center gap-1.5">
              <Lock size={13} /> {isStarter ? "Multi Admin tersedia mulai paket Growth." : "Batas anggota tim untuk paket Anda telah tercapai."}
            </span>
            <Link to="/admin/langganan"><Button size="sm" variant="outline" className="rounded-xl font-bold" data-testid="team-upgrade-btn">Upgrade Paket</Button></Link>
          </div>
        )}
      </Card>

      {/* Members */}
      <Card className="rounded-2xl overflow-hidden mb-4" data-testid="team-members-table">
        <div className="px-4 py-3 border-b font-bold text-sm">Anggota Aktif</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b">
              <th className="px-4 py-2">Nama</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Status</th><th className="px-4 py-2">Bergabung</th><th className="px-4 py-2">Aksi</th>
            </tr></thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b last:border-0" data-testid={`team-member-row-${m.email}`}>
                  <td className="px-4 py-2.5 font-semibold text-xs">
                    {m.name} {m.is_owner && <Badge className="ml-1 bg-[#F9C846] text-[#4A2E1F] text-[9px]" data-testid="owner-badge"><Crown size={9} className="mr-0.5" /> Pemilik</Badge>}
                    {m.id === user?.id && <span className="ml-1 text-[9px] text-[#5C5C5C]">(Anda)</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{m.email}</td>
                  <td className="px-4 py-2.5">
                    {m.is_owner ? <span className="text-xs font-bold">Pemilik</span> : (
                      <select value={m.tenant_role} data-testid={`member-role-select-${m.email}`}
                        onChange={(e) => act(() => api.put(`/admin/team/members/${m.id}/role`, { tenant_role: e.target.value }), "Role diperbarui.")}
                        className="h-8 rounded-lg border border-input bg-background px-2 text-xs">
                        {data.roles.map((r) => <option key={r.role} value={r.role}>{ROLE_LABEL[r.role]}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${m.is_active ? "bg-[#E8F5E9] text-[#2D6A4F]" : "bg-[#FDECEA] text-[#DC2F02]"}`}>
                      {m.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString("id-ID") : "-"}</td>
                  <td className="px-4 py-2.5">
                    {!m.is_owner && (
                      <div className="flex items-center gap-2">
                        <Switch checked={m.is_active} data-testid={`member-status-switch-${m.email}`}
                          onCheckedChange={(v) => act(() => api.put(`/admin/team/members/${m.id}/status`, { is_active: v }), v ? "Anggota diaktifkan." : "Anggota dinonaktifkan.")} />
                        <button className="text-[#DC2F02] hover:opacity-70" data-testid={`member-remove-btn-${m.email}`}
                          onClick={() => { if (window.confirm(`Cabut akses ${m.email}?`)) act(() => api.delete(`/admin/team/members/${m.id}`), "Akses dicabut."); }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pending invitations */}
      <Card className="rounded-2xl overflow-hidden" data-testid="team-invitations-table">
        <div className="px-4 py-3 border-b font-bold text-sm">Undangan Tertunda</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b">
              <th className="px-4 py-2">Email</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Diundang oleh</th>
              <th className="px-4 py-2">Kedaluwarsa</th><th className="px-4 py-2">Aksi</th>
            </tr></thead>
            <tbody>
              {data.pending_invitations.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[#5C5C5C]" data-testid="invitations-empty">Tidak ada undangan tertunda.</td></tr>
              ) : data.pending_invitations.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0" data-testid={`invitation-row-${inv.email}`}>
                  <td className="px-4 py-2.5 text-xs font-semibold">{inv.email}</td>
                  <td className="px-4 py-2.5 text-xs">{ROLE_LABEL[inv.role] || inv.role}</td>
                  <td className="px-4 py-2.5 text-xs">{inv.invited_by_name}</td>
                  <td className="px-4 py-2.5 text-xs">{inv.expires_at ? new Date(inv.expires_at).toLocaleString("id-ID") : "-"}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" data-testid={`invitation-resend-btn-${inv.email}`}
                        onClick={async () => {
                          try {
                            const { data: r } = await api.post(`/admin/team/invitations/${inv.id}/resend`);
                            if (r.invite_url) {
                              try { await navigator.clipboard.writeText(r.invite_url); } catch (_) { /* clipboard blocked */ }
                            }
                            toast.success(r.email_sent ? "Undangan dikirim ulang." : "Link baru disalin ke clipboard (email belum terkirim).");
                            load();
                          } catch (e) { notifyError(e); }
                        }}><RotateCw size={12} /> Kirim Ulang</Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs text-[#DC2F02]" data-testid={`invitation-revoke-btn-${inv.email}`}
                        onClick={() => act(() => api.delete(`/admin/team/invitations/${inv.id}`), "Undangan dibatalkan.")}>Batalkan</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} roles={data.roles} onInvited={load} />
    </AdminLayout>
  );
}
